import React, { useState, useRef, useEffect } from "react";
import * as Tone from "tone";
import {
  Box, Typography, Paper, Button, ButtonBase,
  Dialog, DialogTitle, DialogContent, DialogActions, Alert
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useNoiseMonitor } from "./useNoiseMonitor";
import { useTranslation } from "react-i18next";
import { usePatient } from '../context/patientContext';
import "../styles/screeningTest.css";
import headphoneConfig from '../config/headphoneConfig.json' with { type: 'json' };

const EARS = [
  { label: "Right", pan: 1 },
  { label: "Left", pan: -1 },
];

// The old hardcoded values are used as fallbacks.
const FREQUENCIES_ADULT = [
  { freq: 1000, dBFS: headphoneConfig?.[1000]?.[25] ?? -91 },
  { freq: 2000, dBFS: headphoneConfig?.[2000]?.[25] ?? -90 },
  { freq: 4000, dBFS: headphoneConfig?.[4000]?.[25] ?? -85 },
  { freq: 6000, dBFS: headphoneConfig?.[6000]?.[25] ?? -80 }
];

const FREQUENCIES_CHILD = [
  { freq: 1000, dBFS: headphoneConfig?.[1000]?.[20] ?? -96 },
  { freq: 2000, dBFS: headphoneConfig?.[2000]?.[20] ?? -95 },
  { freq: 4000, dBFS: headphoneConfig?.[4000]?.[20] ?? -90 },
  { freq: 6000, dBFS: headphoneConfig?.[6000]?.[20] ?? -85 }
];

if (!headphoneConfig?.[1000]?.[20]
  || !headphoneConfig?.[1000]?.[25]
  || !headphoneConfig?.[2000]?.[20]
  || !headphoneConfig?.[2000]?.[25]
  || !headphoneConfig?.[4000]?.[20]
  || !headphoneConfig?.[4000]?.[25]
  || !headphoneConfig?.[6000]?.[20]
  || !headphoneConfig?.[6000]?.[25]) {
  console.log('No dB FS value found in the config file for a screening frequency');
}

let FREQUENCIES;

const isCypress = localStorage.getItem('isCypress') === 'true';

const PULSE_MS = isCypress ? 100 : 500;
const GAP_MIN_MS = isCypress ? 100 : 3000;
const GAP_MAX_MS = isCypress ? 200 : 5000;
let dBHL;

export const randGap = () => GAP_MIN_MS + Math.floor(Math.random() * (GAP_MAX_MS - GAP_MIN_MS));

function getAge(birthday) {
  let age;
  if (birthday === "") {
    age = 30;
  } else {
    const dob = new Date(birthday);
    const today = new Date();

    age = today.getFullYear() - dob.getFullYear();

    const hasHadBirthdayThisYear =
      today.getMonth() > dob.getMonth() ||
      (today.getMonth() === dob.getMonth() && today.getDate() >= dob.getDate());

    if (!hasHadBirthdayThisYear) {
      age -= 1;
    }
  }
  return age;
}



function ScreeningTest() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const { patientDraft } = usePatient();

  const [started, setStarted] = useState(false);
  const [pauseSpam, setPauseSpam] = useState(false);
  const [paused, setPause] = useState(false);
  const [isRunning, setIsRunning] = useState(false);

  const [listening, setListening] = useState(false);
  const resultsRef = useRef({});
  const respondedThisTrialRef = useRef(false);
  const abortRef = useRef(false);
  const pauseRef = useRef(false);
  const developerRef = useRef(false);
  const heardCountRef = useRef(0);

  const [noiseModalOpen, setNoiseModalOpen] = useState(false);
  const [noiseTryAgain, setNoiseTryAgain] = useState(false);

  const loudMsRef = useRef(0);
  const lastNoiseTickRef = useRef(null);

  const stopActiveToneRef = useRef(() => { });
  const birthday = patientDraft.demographics.dob;
  const age = getAge(birthday);
  if (age <= 18) {
    FREQUENCIES = FREQUENCIES_CHILD;
    dBHL = 20;
  } else {
    FREQUENCIES = FREQUENCIES_ADULT;
    dBHL = 25;
  }

  const { tooLoud, canResume, estimatedDb, error: noiseError } = useNoiseMonitor({
    enabled: started && isRunning && !abortRef.current && !developerRef.current,
    splOffsetDb: 69.2,
    quietHoldMs: 1200,
    updateHz: 12,
  });

  useEffect(() => {
    if (!(started && isRunning) || abortRef.current || developerRef.current) {
      loudMsRef.current = 0;
      lastNoiseTickRef.current = null;
      setNoiseModalOpen(false);
      setNoiseTryAgain(false);
      return;
    }

    const now = performance.now();
    const last = lastNoiseTickRef.current ?? now;
    const dt = now - last;
    lastNoiseTickRef.current = now;

    if (tooLoud) {
      loudMsRef.current += dt;

      if (loudMsRef.current >= 500 && !noiseModalOpen) {
        pauseRef.current = true;
        setListening(false);
        setNoiseModalOpen(true);
        setNoiseTryAgain(false);
        try {
          stopActiveToneRef.current?.();
        } catch { }
      } else if (noiseModalOpen) {
        pauseRef.current = true;
      }
    } else {
      loudMsRef.current = 0;
    }
  }, [tooLoud, started, isRunning, noiseModalOpen]);

  const handleNoiseResume = () => {
    if (!canResume) {
      setNoiseTryAgain(true);
      return;
    }
    setNoiseTryAgain(false);
    setNoiseModalOpen(false);
    pauseRef.current = false;
  };

  const sleepWithPause = async (ms) => {
    let elapsed = 0;
    const step = 50;

    while (elapsed < ms) {
      if (abortRef.current || developerRef.current) return "aborted";

      while (pauseRef.current) {
        await new Promise((r) => setTimeout(r, step));
      }

      if (abortRef.current || developerRef.current) return "aborted";

      await new Promise((r) => setTimeout(r, step));
      elapsed += step;
    }
  };

  const playSinglePulse = async (freqHz, panValue, dBFS) => {
    respondedThisTrialRef.current = false;
    setListening(true);

    const osc = new Tone.Oscillator(freqHz, "sine");
    const panner = new Tone.Panner(panValue).toDestination();
    osc.volume.value = dBFS;

    stopActiveToneRef.current = () => {
      try { osc.stop(); } catch { }
      try { osc.disconnect(); } catch { }
      try { panner.disconnect(); } catch { }
    };

    try {
      osc.connect(panner);
      osc.start();
      await sleepWithPause(PULSE_MS);
      osc.stop();
    } finally {
      stopActiveToneRef.current = () => { };
      osc.dispose();
      panner.dispose();
    }

    const abortcheck = await sleepWithPause(randGap());
    if (abortcheck === "aborted") return "aborted";

    setListening(false);
    return respondedThisTrialRef.current;
  };

  const stopTest = () => {
    abortRef.current = true;
    pauseRef.current = false;
    setListening(false);

    setNoiseModalOpen(false);
    setNoiseTryAgain(false);

    console.log("test terminated");
  };

  const handleRespond = () => {
    if (listening && respondedThisTrialRef.current) {
      heardCountRef.current += 1;
      if (heardCountRef.current >= 3) {
        pauseRef.current = true;
        setPauseSpam(true);
        respondedThisTrialRef.current = false;
      }
    }

    if (listening && !respondedThisTrialRef.current) {
      respondedThisTrialRef.current = true;
      heardCountRef.current += 1;
    }
  };

  const pauseTest = () => {
    console.log("paused");
    setListening(false);
    pauseRef.current = true;
  };

  const resumeTest = () => {
    console.log("resumed");
    setListening(false);
    pauseRef.current = false;
    heardCountRef.current = 0;
    setPauseSpam(false);
    setPause(false);
  };

  const startTest = async () => {
    if (isRunning) return;

    setIsRunning(true);
    abortRef.current = false;
    developerRef.current = false;
    resultsRef.current = {};
    heardCountRef.current = 0;

    try {
      await Tone.start();

      const makeEmptyResults = () =>
        Object.fromEntries(
          EARS.map(({ label }) => [
            label,
            Object.fromEntries(FREQUENCIES.map(({ freq }) => [freq, false]))
          ])
        );

      resultsRef.current = makeEmptyResults();

      for (let ei = 0; ei < EARS.length; ei++) {
        if (abortRef.current) break;
        if (developerRef.current) break;

        const { label, pan } = EARS[ei];
        const RETEST = [];

        for (let fi = 0; fi < FREQUENCIES.length; fi++) {
          if (abortRef.current) break;
          if (developerRef.current) break;

          const { freq, dBFS } = FREQUENCIES[fi];

          resultsRef.current[label][freq] = false;

          await sleepWithPause(randGap());
          const heard = await playSinglePulse(freq, pan, dBFS);

          if (heard === false) {
            RETEST.push({ freq, dBFS });
          } else if (heard === "aborted") {
            break;
          }

          resultsRef.current[label][freq] = (heard === true);
        }

        for (let ri = 0; ri < RETEST.length; ri++) {
          if (abortRef.current) break;
          if (developerRef.current) break;

          const { freq, dBFS } = RETEST[ri];
          await sleepWithPause(randGap());
          const heard = await playSinglePulse(freq, pan, dBFS);

          resultsRef.current[label][freq] = (heard === true);
        }
      }

      // Without this wait, Cypress will sometimes be unable to locate the
      // test button with its data-listening attribute set to 'false' for
      // the final time before the page is navigated to results.
      if (isCypress) {
        await new Promise((r) => setTimeout(r, 1000));
      }

      if (!abortRef.current) {
        sessionStorage.setItem('screeningResults', JSON.stringify(resultsRef.current));
        navigate("/results/screening", {
          replace: true,
          state: { ...(location.state || {}), from: location.pathname, dBHLTested: dBHL }
        });
      }
    } finally {
      setIsRunning(false);
      setNoiseModalOpen(false);
      setNoiseTryAgain(false);
    }
  };

  const NoiseOverlay = (
    <Dialog open={noiseModalOpen} maxWidth="xs" fullWidth>
      <DialogTitle>{t('screening.tooLoudTitle')}</DialogTitle>
      <DialogContent>
        <Typography className="screening-test-dialog-text">
          {t('screening.waitQuiet')}
        </Typography>

        {typeof estimatedDb === "number" && (
          <Typography variant="body2" className="screening-test-dialog-subtext">
            {t('screening.noiseLevel', { db: estimatedDb.toFixed(0) })}
          </Typography>
        )}

        {noiseError && <Alert severity="error">{noiseError}</Alert>}

        {noiseTryAgain && !canResume && (
          <Alert severity="warning" className="screening-test-dialog-alert">
            {t('screening.tryAgain')}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={handleNoiseResume} disabled={!canResume}>
          {t('screening.resumeTest')}
        </Button>
      </DialogActions>
    </Dialog>
  );

  if (!started && !paused && !pauseSpam) {
    return (
      <>
        <Box className="screening-test-page">
          <Paper elevation={3}  className="screening-test-card screening-test-welcome-card">
            <Typography component="h2" variant="h5" className="screening-test-welcome-title">
              {t('screening.welcomeTitle')}
            </Typography>
            <Typography variant="body1" className="screening-test-welcome-body">
              {t('screening.welcomeBody')}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              data-cy="begin_screening_btn"
              onClick={async () => {
                setStarted(true);
                await startTest();
              }}
            >
              {t('screening.beginTest')}
            </Button>
          </Paper>
        </Box>
        {NoiseOverlay}
      </>
    );
  } else if (paused) {
    return (
      <>
        <Box className="screening-test-page screening-test-page--centered">
          <Paper
            elevation={3}
            className="screening-test-card--centered"
          >
            <Button variant="contained" onClick={() => { resumeTest(); }}>
              {t('screening.resume')}
            </Button>

            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                stopTest();
                navigate("/testflow", {
                  replace: true,
                  state: {
                    ...(location.state || {}),
                    from: location.pathname,
                    step: "test"
                  },
                });
              }}
            >
              {t('common.exit')}
            </Button>
          </Paper>
        </Box>
        {NoiseOverlay}
      </>
    );
  } else if (pauseSpam) {
    return (
      <>
        <Box className="screening-test-page screening-test-page--centered">
          <Paper
            elevation={3}
            className="screening-test-card--centered"
            >
            <Typography
              variant="body2"
              className="screening-test-warning-text"
            >
              {t('screening.spamWarning')}
            </Typography>

            <Button
              variant="contained"
              onClick={() => { resumeTest(); }}
            >
              {t('screening.resume')}
            </Button>
          </Paper>
        </Box>
        {NoiseOverlay}
      </>
    );
  }

  return (
    <>
      <Box className="screening-test-page">
        <Paper elevation={3} className="screening-test-card screening-test-active-card">
          <Box className="screening-test-spacer"/>
          <Typography component="h2" variant="h6" align="center" className="screening-test-instruction">
            {t('screening.pressRedSquare')}
          </Typography>

          <Box className="screening-test-button-wrap">
            <Box className="screening-test-button-wrap">
              <ButtonBase
                data-cy="test_btn"
                data-listening={listening}
                onClick={handleRespond}
                className="screening-test-response-button"
                focusRipple
                aria-label={t('screening.heardToneAria')}
                disabled={noiseModalOpen}
              >
                <Paper
                  elevation={6}
                  className="screening-test-response-square" 
                />
              </ButtonBase>
            </Box>
          </Box>

          <Button
            variant="contained"
            color="secondary"
            onClick={() => { setPause(true); pauseTest(); }}
            className="screening-test-pause-btn"
            disabled={noiseModalOpen}
          >
            {t('screening.pause')}
          </Button>
          {process.env.NODE_ENV === 'development' && <Button
            variant="contained"
            color="secondary"
            onClick={() => { developerRef.current = true }}
            className="screening-test-developer-btn"
            disabled={noiseModalOpen}
          >
            Developer
          </Button>}
        </Paper>
      </Box>
      {NoiseOverlay}
    </>
  );
}

export const __testables = { randGap };
export default ScreeningTest;